"use client";

/**
 * P3-08 · Mobile sticky add-to-cart bar (per P3-DB01).
 *
 * Appears once the inline CTA scrolls out of view and mirrors the primary
 * add-to-cart action, so the buy button is always reachable on a long PDP.
 * Mobile-only (`sm:hidden`); driven by an IntersectionObserver on a sentinel
 * placed next to the inline CTA. Rendered through a portal so it escapes any
 * transformed/overflow ancestor.
 */
import * as React from "react";

import { useTranslator } from "~/i18n/provider";
import { createPortal } from "react-dom";

import type { CartPersonalisation } from "~/lib/cart/cart-interface";
import { formatMoney } from "~/lib/money";

import { AddToCartButton } from "./AddToCartButton";

export function StickyBuyBar({
  sentinelRef,
  title,
  variantId,
  quantity,
  personalisation,
  disabled,
  reason,
  priceMinor,
  currency,
}: {
  sentinelRef: React.RefObject<HTMLElement | null>;
  title: string;
  variantId: string;
  quantity: number;
  personalisation: CartPersonalisation;
  disabled: boolean;
  reason: string | null;
  /** Selected variant's unit price; null before a variant resolves. */
  priceMinor: number | null;
  currency: string;
}) {
  const t = useTranslator();
  const [mounted, setMounted] = React.useState(false);
  const [offscreen, setOffscreen] = React.useState(false);

  React.useEffect(() => setMounted(true), []);

  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setOffscreen(!entry!.isIntersecting),
      { rootMargin: "0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [sentinelRef]);

  if (!mounted || !offscreen) return null;

  return createPortal(
    <section
      data-testid="pdp-sticky-bar"
      aria-label={t("pdp.addToCart")}
      // `pb` adds the home-indicator safe area on top of the base padding — needs
      // viewport-fit=cover (set in the root layout) to resolve to non-zero.
      className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-border bg-surface/95 px-3 pt-3 backdrop-blur sm:hidden [padding-bottom:calc(0.75rem+env(safe-area-inset-bottom))]"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-fg">{title}</p>
        {priceMinor !== null ? (
          <p className="text-sm font-semibold tabular-nums text-fg">
            {formatMoney(priceMinor, currency)}
          </p>
        ) : null}
      </div>
      <div className="w-40 shrink-0">
        <AddToCartButton
          variantId={variantId}
          quantity={quantity}
          personalisation={personalisation}
          disabled={disabled}
          reason={reason}
          source="sticky_bar"
          testId="pdp-sticky-add-to-cart"
        />
      </div>
    </section>,
    document.body,
  );
}
